// Library Methods
import { useMemo } from "react";
import { DateTime } from "luxon";

// Components
import {
  DateView,
  DesktopDatePicker,
  MobileDatePicker,
  pickersMonthClasses,
  pickersYearClasses,
} from "@mui/x-date-pickers";

// Styles
import useMediaQuery from "@mui/material/useMediaQuery";
import { createTheme, useTheme } from "@mui/material/styles";
import {
  CustomTextFieldProps,
  getSurfaceTokens,
  getPopoverPaperSx,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type CustomDatePickerType = {
  date: DateTime | null;
  hasError?: boolean;
  handleChange: (value: DateTime | null) => void;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  helperText?: string;
  openTo?: DateView;
  minDay?: DateTime;
};

const CustomDatePicker = ({
  date,
  handleChange = (_) => {},
  disabled = false,
  disableFuture = false,
  disablePast = false,
  hasError = false,
  helperText = "",
  openTo = "day",
  minDay = undefined,
}: CustomDatePickerType) => {
  const { clinicInfo } = useClinicContext();
  const appTheme = useTheme();
  const { inputBorder } = getSurfaceTokens(appTheme);
  const popperPaperSx = useMemo(() => getPopoverPaperSx(appTheme), [appTheme]);

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const PickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;

  const localEndOfDay = useMemo(
    () =>
      DateTime.now()
        .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
        .endOf("day"),
    [clinicInfo?.timezone]
  );

  const localStartOfDay = useMemo(
    () =>
      DateTime.now()
        .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
        .startOf("day"),
    [clinicInfo?.timezone]
  );

  const localMinDay = useMemo(
    () =>
      minDay ??
      DateTime.fromObject({
        year: 1900,
        month: 1,
        day: 1,
      })
        .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
        .startOf("day"),
    [minDay, clinicInfo?.timezone]
  );

  const commonProps = useMemo(
    () => ({
      value: date,
      format: "yyyy-MM-dd",
      disableFuture,
      disablePast,
      disabled,
      onChange: handleChange,
      slotProps: {
        // DesktopDatePicker renders its calendar in a Popper (desktopPaper);
        // MobileDatePicker renders it in a Dialog (mobilePaper) instead —
        // both are supplied since PickerComponent switches between them.
        desktopPaper: { sx: popperPaperSx },
        mobilePaper: { sx: popperPaperSx },
        openPickerButton: { disableRipple: true },
        layout: {
          sx: {
            [`& .${pickersYearClasses.yearButton}.${pickersYearClasses.selected}`]:
              {
                backgroundColor: "#E2772E !important",
              },
            [`& .${pickersYearClasses.yearButton}`]: {
              borderRadius: "4px",
            },
            [`& .${pickersMonthClasses.monthButton}.${pickersMonthClasses.selected}`]:
              {
                backgroundColor: "#E2772E !important",
              },
            [`& .${pickersMonthClasses.monthButton}`]: {
              borderRadius: "4px",
            },
          },
        },
        day: {
          sx: {
            "&.MuiPickersDay-root.Mui-selected": {
              backgroundColor: "#E2772E !important",
            },
            "&.MuiPickersDay-root": {
              borderRadius: "4px",
            },
          },
        },
        textField: {
          fullWidth: true,
          required: true,
          error: hasError,
          helperText,
          inputProps: {
            "data-testid": "custom-date-picker",
            sx: {
              fontSize: "14px",
              "&::placeholder": {
                color: inputBorder,
                opacity: 1,
              },
            },
          },
          sx: {
            "& .MuiFormHelperText-root": {
              margin: "4px 0px 0px 0px",
              fontSize: "11px",
            },
            "& .MuiInputBase-root": {
              ...CustomTextFieldProps(appTheme),
            },
            "& .MuiOutlinedInput-input": {
              padding: "0px 16px",
            },
            "& .MuiButtonBase-root": {
              color: appTheme.palette.mode === "dark" ? "#FFF" : "#5B6D7A",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: "#E2772E",
              },
            },
          },
        },
      },
    }),
    [
      popperPaperSx,
      appTheme,
      inputBorder,
      date,
      disableFuture,
      disablePast,
      handleChange,
      hasError,
      disabled,
      helperText,
    ]
  );
  return (
    <PickerComponent
      maxDate={
        disableFuture ? localEndOfDay : localEndOfDay.plus({ years: 50 })
      }
      minDate={disablePast ? localStartOfDay : localMinDay}
      openTo={openTo}
      views={["year", "month", "day"]}
      {...commonProps}
    />
  );
};

export default CustomDatePicker;
